import { del, get, post, put, toQueryParams } from '@/lib/http';
import { endpoints } from './endpoints';
import type {
  Buyer,
  LotNumber,
  Page,
  PageRequest,
  PurchaseOrder,
  Shipment,
  ShipmentStatus,
  Size,
  Style,
  Warehouse,
} from '@/types';

export interface BuyerQuery extends PageRequest {
  search?: string;
  active?: boolean;
}

export interface PoQuery extends PageRequest {
  buyerId?: number;
  status?: string;
  search?: string;
  active?: boolean;
}

export interface StyleQuery extends PageRequest {
  poId?: number;
  search?: string;
  active?: boolean;
}

export interface LotNumberQuery extends PageRequest {
  poId?: number;
  styleId?: number;
  search?: string;
  active?: boolean;
}

export interface SizeQuery extends PageRequest {
  poId?: number;
  styleId?: number;
  lotNumberId?: number;
  search?: string;
  active?: boolean;
}

export interface CreateBuyerBody {
  name: string;
}

export interface CreatePurchaseOrderBody {
  buyerId: number;
  poNumber: string;
}

export interface CreateStyleBody {
  poId: number;
  styleNo: string;
}

export interface CreateLotNumberBody {
  poId: number;
  styleId: number;
  lotNumber: string;
}

export interface CreateSizeBody {
  poId: number;
  styleId: number;
  lotNumberId: number;
  size: string;
}

export interface CreateShipmentBody {
  poId: number;
  /** Optional — auto-generated (SHP-yyMMdd-NNN) when left blank. */
  shipmentNo?: string;
  etd?: string;
  destination?: string;
  forwarder?: string;
  status?: ShipmentStatus;
}

export const masterApi = {
  /* --------------------------------- buyers -------------------------------- */
  buyers: (query: BuyerQuery = {}) =>
    get<Page<Buyer>>(endpoints.master.buyers, { params: toQueryParams({ size: 100, ...query }) }),
  buyer: (id: number) => get<Buyer>(endpoints.master.buyer(id)),
  createBuyer: (body: CreateBuyerBody) => post<Buyer>(endpoints.master.buyers, body),
  updateBuyer: (id: number, body: CreateBuyerBody & { active?: boolean }) =>
    put<Buyer>(endpoints.master.buyer(id), body),
  deleteBuyer: (id: number) => del<void>(endpoints.master.buyer(id)),

  /* ----------------------------- purchase orders ---------------------------- */
  purchaseOrders: (query: PoQuery = {}) =>
    get<Page<PurchaseOrder>>(endpoints.master.purchaseOrders, {
      params: toQueryParams({ size: 100, ...query }),
    }),
  purchaseOrder: (id: number) => get<PurchaseOrder>(endpoints.master.purchaseOrder(id)),
  createPurchaseOrder: (body: CreatePurchaseOrderBody) =>
    post<PurchaseOrder>(endpoints.master.purchaseOrders, body),
  updatePurchaseOrder: (id: number, body: Partial<CreatePurchaseOrderBody> & { active?: boolean }) =>
    put<PurchaseOrder>(endpoints.master.purchaseOrder(id), body),
  deletePurchaseOrder: (id: number) => del<void>(endpoints.master.purchaseOrder(id)),

  /* --------------------------------- styles --------------------------------- */
  /** Styles of a PO — used to cascade the QR generation form. */
  styles: (poId: number) => get<Style[]>(endpoints.master.styles, { params: { poId } }),
  stylesPage: (query: StyleQuery = {}) =>
    get<Page<Style>>(endpoints.master.styles, { params: toQueryParams({ size: 100, ...query }) }),
  createStyle: (body: CreateStyleBody) => post<Style>(endpoints.master.styles, body),
  updateStyle: (id: number, body: Partial<CreateStyleBody> & { active?: boolean }) =>
    put<Style>(endpoints.master.style(id), body),
  deleteStyle: (id: number) => del<void>(endpoints.master.style(id)),

  /* ------------------------------- lot numbers ------------------------------ */
  lotNumbers: (styleId: number) =>
    get<LotNumber[]>(endpoints.master.lotNumbers, { params: { styleId } }),
  lotNumbersPage: (query: LotNumberQuery = {}) =>
    get<Page<LotNumber>>(endpoints.master.lotNumbers, {
      params: toQueryParams({ size: 100, ...query }),
    }),
  createLotNumber: (body: CreateLotNumberBody) =>
    post<LotNumber>(endpoints.master.lotNumbers, body),
  updateLotNumber: (id: number, body: Partial<CreateLotNumberBody> & { active?: boolean }) =>
    put<LotNumber>(endpoints.master.lotNumber(id), body),
  deleteLotNumber: (id: number) => del<void>(endpoints.master.lotNumber(id)),

  /* ---------------------------------- sizes ---------------------------------- */
  sizes: (lotNumberId: number) => get<Size[]>(endpoints.master.sizes, { params: { lotNumberId } }),
  sizesPage: (query: SizeQuery = {}) =>
    get<Page<Size>>(endpoints.master.sizes, { params: toQueryParams({ size: 100, ...query }) }),
  createSize: (body: CreateSizeBody) => post<Size>(endpoints.master.sizes, body),
  updateSize: (id: number, body: Partial<CreateSizeBody> & { active?: boolean }) =>
    put<Size>(endpoints.master.size(id), body),
  deleteSize: (id: number) => del<void>(endpoints.master.size(id)),

  /* ------------------------------- warehouses -------------------------------- */
  warehouses: () => get<Warehouse[]>(endpoints.master.warehouses),

  /* -------------------------------- shipments -------------------------------- */
  /** Not paginated server-side — one workspace's shipment count is small enough to page client-side. */
  shipments: (poId?: number) => get<Shipment[]>(endpoints.shipments.list, { params: poId ? { poId } : undefined }),
  createShipment: (body: CreateShipmentBody) => post<Shipment>(endpoints.shipments.list, body),
  updateShipment: (id: number, body: Partial<CreateShipmentBody>) =>
    put<Shipment>(endpoints.shipments.byId(id), body),
};
